import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

export type Ga4FrontendEventName =
  | 'search'
  | 'view_search_results'
  | 'view_item_list'
  | 'select_item'
  | 'view_item'
  | 'select_room'
  | 'filter_apply'
  | 'sort_apply'
  | 'form_error';

export interface Ga4FormError {
  field_name: string;
  error_type: string;
}

export interface Ga4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
  price?: number;
  quantity?: number;
}

export interface FrontendAnalyticsParams {
  search_id?: string;
  hotel_id?: string;
  hotel_name?: string;
  destination_id?: string;
  destination_name?: string;
  room_id?: string;
  room_name?: string;
  rate_plan_id?: string;
  check_in_date?: string;
  check_out_date?: string;
  nights?: number;
  room_count?: number;
  adult_count?: number;
  child_count?: number;
  value_toman?: number;
  value?: number;
  currency?: 'IRR';
  [key: string]: unknown;
}

interface EcommerceData {
  currency: 'IRR';
  value?: number;
  items: Ga4Item[];
}

interface SearchContext {
  search_id: string;
  created_at: number;
}

interface Ga4DebugMessage {
  stage: 'track_search_called' | 'track_search_deduplicated' | 'data_layer_pushed';
  event: Ga4FrontendEventName;
  payload: unknown;
  timestamp: number;
}

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
  /**
   * Opt-in DevTools hook. It is inert until assigned from the browser console.
   */
  __ihoGa4Debug?: (message: Ga4DebugMessage) => void;
}

const SEARCH_CONTEXT_KEY = 'iho.ga4.search_context';
const PII_KEYS = new Set([
  'first_name',
  'last_name',
  'full_name',
  'mobile',
  'phone',
  'phone_number',
  'email',
  'national_id',
  'national_code',
  'card_number',
  'bank_account',
]);

@Injectable({ providedIn: 'root' })
export class Ga4AnalyticsService {
  private readonly isBrowser: boolean;
  private searchContext?: SearchContext;
  private lastSearchSignature = '';
  private lastSearchAt = 0;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.searchContext = this.readSearchContext();
  }

  /**
   * مبلغ ریالی API را به پارامترهای مالی Measurement Plan تبدیل می‌کند.
   * خروجی: value به ریال، value_toman به تومان و currency با مقدار ثابت IRR.
   */
  static rialAmount(
    value: number | null | undefined
  ): Pick<FrontendAnalyticsParams, 'value' | 'value_toman' | 'currency'> {
    const rial = Number(value);
    if (!Number.isFinite(rial) || rial < 0) return {};
    return { value: rial, value_toman: Math.ceil(rial / 10), currency: 'IRR' };
  }

  get currentSearchId(): string | undefined {
    return this.searchContext?.search_id;
  }

  /**
   * event: search
   * ورودی فعلی: destination_id، destination_name، search_term، check_in_date و check_out_date.
   * سرویس یک search_id یکتا تولید می‌کند و برای اتصال مراحل بعدی Funnel در sessionStorage نگه می‌دارد.
   */
  trackSearch(params: FrontendAnalyticsParams): string {
    this.notifyDebugger('track_search_called', 'search', this.clean(params));
    const signature = JSON.stringify([
      params.destination_id,
      params.destination_name,
      params.check_in_date,
      params.check_out_date,
    ]);
    if (
      signature === this.lastSearchSignature &&
      Date.now() - this.lastSearchAt < 1000 &&
      this.currentSearchId
    ) {
      this.notifyDebugger('track_search_deduplicated', 'search', {
        ...(this.clean(params) as Record<string, unknown>),
        search_id: this.currentSearchId,
      });
      return this.currentSearchId;
    }

    const searchId = this.createId();
    this.lastSearchSignature = signature;
    this.lastSearchAt = Date.now();
    this.searchContext = { search_id: searchId, created_at: Date.now() };
    this.writeSearchContext();
    this.push('search', { ...params, search_id: searchId });
    return searchId;
  }

  /**
   * event: view_search_results
   * ورودی فعلی: مشخصات مقصد و تاریخ از Search، به‌همراه result_count و no_results.
   * search_id جاری به‌صورت خودکار به payload اضافه می‌شود.
   */
  trackViewSearchResults(params: FrontendAnalyticsParams & { result_count: number }): void {
    this.push('view_search_results', this.withSearchId(params));
  }

  /**
   * event: view_item_list
   * params شامل مقصد، تاریخ و تعداد شب است؛ items شامل شناسه، نام، نوع، شهر، جایگاه و قیمت هر هتل است.
   * items داخل ecommerce با currency برابر IRR ارسال می‌شوند.
   */
  trackViewItemList(params: FrontendAnalyticsParams, items: Ga4Item[]): void {
    this.push(
      'view_item_list',
      this.withSearchId(params),
      { currency: 'IRR', items }
    );
  }

  /**
   * event: select_item
   * params شامل اطلاعات هتل، مقصد، تاریخ، شب و مبلغ است؛ item مشخصات ecommerce هتل انتخاب‌شده را دارد.
   */
  trackSelectItem(params: FrontendAnalyticsParams, item: Ga4Item): void {
    this.push(
      'select_item',
      this.withSearchId(params),
      { currency: 'IRR', value: item.price, items: [item] }
    );
  }

  /**
   * event: view_item
   * params شامل اطلاعات هتل، مقصد، تاریخ، شب و مبلغ است؛ item مشخصات ecommerce هتل مشاهده‌شده را دارد.
   */
  trackViewItem(params: FrontendAnalyticsParams, item: Ga4Item): void {
    this.push(
      'view_item',
      this.withSearchId(params),
      { currency: 'IRR', value: item.price, items: [item] }
    );
  }

  /**
   * event: select_room
   * ورودی فعلی: هتل/مقصد، اتاق، تاریخ، شب، تعداد اتاق، ظرفیت بزرگسال و مبلغ ریالی/تومانی.
   */
  trackSelectRoom(params: FrontendAnalyticsParams): void {
    this.push('select_room', this.withSearchId(params));
  }

  /**
   * event: filter_apply | sort_apply
   * برای فیلتر filter_name و filter_value و برای مرتب‌سازی sort_type دریافت می‌کند.
   */
  trackDiagnostic(event: 'filter_apply' | 'sort_apply', params: FrontendAnalyticsParams): void {
    this.push(event, this.withSearchId(params));
  }

  /**
   * event: form_error
   * فقط نام فنی فرم/فیلد و نوع خطا را می‌پذیرد؛ مقدار واردشده کاربر هرگز ارسال نمی‌شود.
   * برای هر فیلد نامعتبر یک event مستقل ثبت می‌شود تا گزارش خطاها در GA4 قابل تفکیک باشد.
   */
  trackFormErrors(
    formName: string,
    pageType: 'php' | 'plp' | 'pdp',
    errors: readonly Ga4FormError[],
    params: FrontendAnalyticsParams = {}
  ): void {
    errors.forEach(({ field_name, error_type }) => {
      this.push(
        'form_error',
        this.withSearchId({
          ...params,
          form_name: formName,
          page_type: pageType,
          field_name,
          error_type,
        })
      );
    });
  }

  private push(
    event: Ga4FrontendEventName,
    params: FrontendAnalyticsParams,
    ecommerce?: EcommerceData
  ): boolean {
    const windowRef = this.windowRef;
    if (!windowRef) return false;

    windowRef.dataLayer = windowRef.dataLayer || [];
    // پیش از eventهای ecommerce داده قبلی پاک می‌شود تا items رویداد قبلی وارد event جدید نشوند.
    if (ecommerce) windowRef.dataLayer.push({ ecommerce: null });
    const payload = this.clean({
      event,
      ...params,
      ...(ecommerce ? { ecommerce } : {}),
    }) as Record<string, unknown>;
    windowRef.dataLayer.push(payload);
    this.notifyDebugger('data_layer_pushed', event, payload);

    return true;
  }

  private withSearchId(params: FrontendAnalyticsParams): FrontendAnalyticsParams {
    // اگر call site شناسه مشخصی ندهد، search_id آخرین جست‌وجوی همین session استفاده می‌شود.
    return { search_id: params.search_id ?? this.currentSearchId, ...params };
  }

  private clean(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.clean(item)).filter((item) => item !== undefined);
    }
    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
        (result, [key, item]) => {
          // هیچ‌کدام از کلیدهای PII حتی در صورت ارسال اشتباه call site وارد dataLayer نمی‌شوند.
          if (PII_KEYS.has(key.toLowerCase())) return result;
          const cleaned = this.clean(item);
          if (cleaned !== undefined) result[key] = cleaned;
          return result;
        },
        {}
      );
    }
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number' && !Number.isFinite(value)) return undefined;
    return value;
  }

  private createId(): string {
    const cryptoRef = this.windowRef?.crypto;
    if (cryptoRef && 'randomUUID' in cryptoRef) return cryptoRef.randomUUID();
    return `search-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private notifyDebugger(
    stage: Ga4DebugMessage['stage'],
    event: Ga4FrontendEventName,
    payload: unknown
  ): void {
    try {
      this.windowRef?.__ihoGa4Debug?.({
        stage,
        event,
        payload,
        timestamp: Date.now(),
      });
    } catch {
      // Debug tooling must never interrupt analytics or the user flow.
    }
  }

  private readSearchContext(): SearchContext | undefined {
    try {
      const value = this.windowRef?.sessionStorage.getItem(SEARCH_CONTEXT_KEY);
      if (!value) return undefined;
      const context = JSON.parse(value) as SearchContext;
      return Date.now() - context.created_at <= 30 * 60 * 1000 ? context : undefined;
    } catch {
      return undefined;
    }
  }

  private writeSearchContext(): void {
    try {
      this.windowRef?.sessionStorage.setItem(SEARCH_CONTEXT_KEY, JSON.stringify(this.searchContext));
    } catch {
      // Analytics must never block the user flow.
    }
  }

  private get windowRef(): DataLayerWindow | undefined {
    return this.isBrowser ? (this.document.defaultView as DataLayerWindow | null) ?? undefined : undefined;
  }
}
